import React from 'react';
import { Edge, Node, NodeMouseHandler, useReactFlow } from 'reactflow';
import { Box, Typography } from '@mui/material';
import toastr from 'react-hot-toast';
import SparkBuilderCanvas from './SparkBuilderCanvas';
import { SelectStepTypeModal } from './Modals';
import {
  createSampleSparkButtonEdge,
  REACT_FLOW_BOUNDS,
} from './SparkBuilderCanvas/hooks';
import { useBatchSparkStepOrder } from 'hooks/SparkBuilder/sparkStep';
import { useSparkBuilderStore } from 'stores';
import type { NewStepOrdersType } from './SparkBuilderCanvas/types';
import { useDialog } from 'hooks/common';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { WarningStatusIcon } from 'components/SVG';
import { useCommonActionHelper } from 'stores/sparkBuilderStore';

function SparkStepsContainer() {
  const { getEdges, getNodes, fitBounds } = useReactFlow();

  const {
    open: isRefModalOpen,
    openDialog: openRefModal,
    closeDialog: closeRefModal,
  } = useDialog();

  const chartNodes = getNodes();
  const chartEdges = getEdges();

  const setStepOrder = useSparkBuilderStore(
    (store) => store.canvasActions.setStepOrder
  );

  const isReferenceBreak = useSparkBuilderStore(
    React.useCallback((store) => store.isReferenceBreak, [])
  );

  const setIsReferenceBreak = useSparkBuilderStore(
    React.useCallback(
      (store) => store.sparkStepsActions.setIsReferenceBreak,
      []
    )
  );
  const onOrderUpdate = useCommonActionHelper();

  const isAddDialogOpen = useSparkBuilderStore(
    (store) => store.canvas.isAddDialogOpen
  );
  const previousState = useSparkBuilderStore(
    (store) => store.sparkSteps.previousData
  );

  const updateReactFlowElements = useSparkBuilderStore((store) =>
    store.canvasActions.useReactFlowElements()
  );

  const sparkSampleEdge = createSampleSparkButtonEdge();
  const { mutateAsync: createSparkStepOrder } = useBatchSparkStepOrder();
  React.useEffect(() => {
    if (isReferenceBreak) {
      openRefModal();
      setIsReferenceBreak(false);
    }
  }, [isReferenceBreak, openRefModal, setIsReferenceBreak]);

  React.useEffect(() => {
    fitBounds(REACT_FLOW_BOUNDS);
  }, [fitBounds]);

  React.useEffect(() => {
    if (!chartNodes?.length && !chartEdges?.length) {
      updateReactFlowElements();
    }
  }, [chartEdges?.length, chartNodes?.length, updateReactFlowElements]);

  const handleRefDialogConfirm = () => {
    setIsReferenceBreak(false);
    closeRefModal();
    onOrderUpdate.onSuccess();
  };

  const handleRefDialogCancel = () => {
    setIsReferenceBreak(false);
    closeRefModal();
    if (previousState?.length) {
      const previousOrder: NewStepOrdersType[] = previousState.map((step) => ({
        sparkStepID: step.id,
        stepOrder: step.attributes.stepOrder,
      }));
      createSparkStepOrder(previousOrder);
    }
    //Calling onError just to recreate the canvas
    onOrderUpdate.onError();
    onOrderUpdate.invalidateQuery();
  };

  const onEdgeClickHandle = (
    _: React.MouseEvent<Element, MouseEvent>,
    edge: Edge,
    nodes: Node[]
  ) => {
    const edgeTarget = edge.target;
    const targetNode = nodes.find((node) => node.id === edgeTarget);
    const nextStepOrder = targetNode?.data.stepOrder;
    setStepOrder(nextStepOrder);
  };

  const onNodeClickHandle: NodeMouseHandler = (_, node) => {};

  const onNodeDropHandler = async (
    node: Node,
    nodes: Node[],
    isDraggedAtLeastMinimumDistance: boolean,
    isDragSuccess: boolean
  ) => {
    if (isDragSuccess) {
      //remove any nodes that does not have stepID
      const filteredNodes: Node[] = nodes.filter((node) => node.data.stepID);
      const sparkStepOrder: NewStepOrdersType[] = filteredNodes?.map(
        (node, index) => {
          return {
            sparkStepID: node?.data?.stepID || '',
            stepOrder: index + 1,
          };
        }
      );

      createSparkStepOrder(sparkStepOrder);
    } else if (!isDraggedAtLeastMinimumDistance) {
    } else {
      toastr.error('Unable to reorder spark step');
    }
  };

  return (
    <Box
      sx={{ flexGrow: 1 }}
      display='flex'
      flexDirection='column'
      alignItems='center'
      height='calc(100vh - 4rem)'
      maxWidth={'100%'}
      marginLeft='4rem'
    >
      <SparkBuilderCanvas
        onEdgeClickHandle={onEdgeClickHandle}
        onNodeClickHandle={onNodeClickHandle}
        onNodeDropHandler={onNodeDropHandler}
        sparkSampleEdge={sparkSampleEdge}
      />
      {isAddDialogOpen && <SelectStepTypeModal open={isAddDialogOpen} />}
      {isRefModalOpen && (
        <ConfirmationDialog
          open={isRefModalOpen}
          title='Confirm'
          onClose={handleRefDialogCancel}
          primaryButtonText='Continue'
          secondaryButtonText='Cancel'
          onPrimaryButtonClick={handleRefDialogConfirm}
          dialogStyles={{
            '& .MuiPaper-root': { minWidth: '40%' },
          }}
          hideTitle={true}
          confirmationMessage={
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              flexDirection='column'
              gap={2}
            >
              <WarningStatusIcon width='100px' height='100px' />
              <Typography variant='h5' fontFamily='Mont-Semibold'>
                Move Step?
              </Typography>
              <Typography>This step has references which may break</Typography>
            </Box>
          }
        />
      )}
    </Box>
  );
}
export default SparkStepsContainer;
